<template>
  <aha-context-menu
    ref="canvas-context-menu"
    :x="targetX"
    :y="targetY"
    :offsetX="offsetX"
    :offsetY="offsetY"
    :layerX="layerX"
    :layerY="layerY"
    :targetClick="targetClick"
    isMenuInCanvas
    :target="'slide-container'"
    class="canvas-right-click-menu"
  >
    <aha-context-menu-item
      @click="handlePasteBlock()"
      class="canvas-right-click-menu-item __undo"
      v-if="isContentSlideType"
      :disabled="!isPasteBlockEnabled"
      hasDivider
    >
      <div class="space-x-2">
        <aha-icon
          name="paste"
          class="text-base-70"
          width="24"
          height="24"
          viewBox="0 0 20 20"
        />
        <span>{{ $i18nPresenter.t('Paste') }}</span>
      </div>
      <aha-shortcut-key keyName="V" hasCtrl />
    </aha-context-menu-item>
    <aha-context-menu-item
      v-if="isUndoRedoVisible"
      :disabled="isUndoDisabled"
      @click="handleUndoAction()"
      class="canvas-right-click-menu-item __undo"
    >
      <div class="space-x-2">
        <aha-icon
          name="undo"
          class="text-base-70"
          width="24"
          height="24"
          viewBox="0 0 16 16"
        />
        <span>{{ $i18nPresenter.t('Undo') }}</span>
      </div>
      <aha-shortcut-key keyName="Z" hasCtrl />
    </aha-context-menu-item>
    <aha-context-menu-item
      v-if="isUndoRedoVisible"
      :disabled="isRedoDisabled"
      @click="handleRedoAction()"
      class="canvas-right-click-menu-item __redo"
      hasDivider
    >
      <div class="space-x-2">
        <aha-icon
          name="redo"
          class="text-base-70"
          width="24"
          height="24"
          viewBox="0 0 16 16"
        />
        <span>{{ $i18nPresenter.t('Redo') }}</span>
      </div>
      <aha-shortcut-key keyName="Z" hasCtrl hasShift />
    </aha-context-menu-item>
    <aha-context-menu-item
      class="canvas-right-click-menu-item"
      v-if="isRightClickAddImageVisible"
      @click="handleOpenAddImageModal()"
    >
      <aha-icon name="image" class="text-base-70" />
      <span>{{ addImageText }}</span>
    </aha-context-menu-item>
    <aha-context-menu-item
      class="canvas-right-click-menu-item"
      v-if="isRightClickAddAudioVisible"
      @click="handleOpenAudioSettingModal()"
      hasDivider
    >
      <aha-icon name="music-note" class="text-base-70" />
      <span>{{ addAudioText }}</span>
    </aha-context-menu-item>
    <aha-context-menu-item
      v-if="canViewThemeGallery"
      class="pb-3 canvas-right-click-menu-item"
      @click="redirectToCustomiseTab()"
    >
      <aha-icon
        name="palette-2"
        class="text-base-70"
        width="24"
        height="24"
        viewBox="0 0 20 20"
      />
      <span>{{ $i18nPresenter.t('Design') }}</span>
    </aha-context-menu-item>
    <aha-context-menu-item
      class="pb-3 canvas-right-click-menu-item"
      v-if="!canViewThemeGallery"
      @click="redirectToChartColorTab()"
    >
      <aha-icon
        name="paint"
        class="mb-1 text-base-70"
        width="22"
        height="22"
        viewBox="0 0 20 20"
      />
      <span>{{ $i18nPresenter.t('Change background') }}</span>
    </aha-context-menu-item>
    <aha-context-menu-item
      v-if="isPresetConfigurable && !canViewThemeGallery"
      class="pb-3 canvas-right-click-menu-item"
      name="context-menu-item-chart-color"
      v-aha-click="() => redirectToCustomiseTab()"
    >
      <i class="text-base-70 text-2xl far fa-chart-bar"></i>
      <span>{{ $i18nPresenter.t('Chart colours') }}</span>
    </aha-context-menu-item>
  </aha-context-menu>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import EventBus from '@/eventBus'
import utils from '@/util'
import eventCategories from '@/constant/eventCategories'
import { isFocusingOnAnInput } from '@/utils/dom.utils'
import RightNavTabs from '@/constant/RightNavTabs'
import redirectMixin from '@/mixins/redirect'

const { SUB_TABS } = RightNavTabs

class SVGPathElement extends HTMLElement {}

window.SVGPathElement = SVGPathElement

export default {
  components: {
  },
  mixins: [redirectMixin],
  props: {
    targetX: {
      type: Number,
      default: null,
    },
    targetY: {
      type: Number,
      default: null,
    },
    offsetX: {
      type: Number,
      default: null,
    },
    offsetY: {
      type: Number,
      default: null,
    },
    layerX: {
      type: Number,
      default: null,
    },
    layerY: {
      type: Number,
      default: null,
    },
    targetClick: {
      type: [HTMLSpanElement, HTMLImageElement, HTMLDivElement, HTMLCanvasElement,
        SVGPathElement, HTMLElement, HTMLParagraphElement, SVGTextElement, SVGSVGElement],
      default: null,
    },
  },
  data() {
    return {
      gaLabel: 'Canvas > Right click > Open canvas context menu',
    }
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      plan: (state) => state.plan,
      clipboardBlocks: (state) => state.clipboardBlocks,
    }),
    ...mapState([
      'undoHistory',
      'redoHistory',
      'isBlockHistory',
      'isSyncing',
      'isDisableEditPresentation',
    ]),
    ...mapGetters(['selectedSlide']),
    ...mapGetters('rightNav', ['rightNavTabNames']),
    ...mapGetters('slideType', ['isPresetConfigurable']),
    ...mapGetters('themeModule', ['canViewThemeGallery']),
    isRightClickAddImageVisible() {
      const slideTypes = ['freestyle', 'textSlide', 'bulletPoints', 'qrcode', 'youtube', 'document', 'googleSlides', 'leaderboard', 'spinnerWheel']
      return !slideTypes.includes(this.slideActive.type)
    },
    isRightClickAddAudioVisible() {
      const excludedSlideTypes = ['qrcode', 'youtube', 'googleSlides', 'spinnerWheel']
      return !excludedSlideTypes.includes(this.slideActive.type)
    },
    isUndoRedoVisible() {
      const excludedSlideTypes = ['qrcode', 'googleSlides']
      return !excludedSlideTypes.includes(this.slideActive.type)
    },
    isUndoDisabled() {
      return !this.undoHistory.length || this.isBlockHistory ||
        this.isSyncing !== 2 || this.isDisableEditPresentation
    },
    isRedoDisabled() {
      return !this.redoHistory.length || this.isBlockHistory ||
        this.isSyncing !== 2 || this.isDisableEditPresentation
    },
    addAudioText() {
      return this.selectedSlide.audioLink ? 'Change audio' : 'Add audio'
    },
    addImageText() {
      return this.slideActive.image ? 'Change image' : 'Add image'
    },
    isContentSlideType() {
      return this.slideActive.type === 'freestyle'
    },
    isPasteBlockEnabled() {
      if (isFocusingOnAnInput()) return false
      return this.clipboardBlocks &&
      this.clipboardBlocks.length > 0
    },
  },

  mounted() {
    EventBus.$on('canvas-right-click-menu: show', () => {
      this.$refs['canvas-context-menu'].show()
    })
    EventBus.$on('canvas-right-click-menu: hide', () => {
      this.$refs['canvas-context-menu'].hide()
    })
  },
  destroyed() {
    EventBus.$off('canvas-right-click-menu: show')
    EventBus.$off('canvas-right-click-menu: hide')
  },
  methods: {
    ...mapActions(['updateSlideActiveAction']),
    ...mapActions([
      'undoAction',
      'redoAction',
    ]),
    ...mapActions('rightNav', ['setRightNavActiveTabAction']),
    handlePasteBlock() {
      EventBus.$emit('right-click-paste-block')
    },
    redirectToChartColorTab() {
      this.setRightNavActiveTabAction(this.rightNavTabNames.DESIGN)
      this.redirectTo({
        query: { activeTab: SUB_TABS[this.rightNavTabNames.DESIGN].PRESENTATION },
      })
    },
    redirectToCustomiseTab() {
      this.setRightNavActiveTabAction(this.rightNavTabNames.DESIGN)
      this.redirectTo({
        query: { activeTab: SUB_TABS[this.rightNavTabNames.DESIGN].SLIDES },
      })
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        this.gaLabel,
        'Change background',
      )
    },
    handleUndoAction() {
      this.undoAction()
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        this.gaLabel,
        'Undo',
      )
    },
    handleRedoAction() {
      this.redoAction()
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        this.gaLabel,
        'Redo',
      )
    },
    handleOpenAddImageModal() {
      this.setRightNavActiveTabAction(this.rightNavTabNames.CONTENT)
      setTimeout(() => {
        if (this.slideActive.type === 'image') {
          EventBus.$emit('open-add-image-modal')
        } else if (!this.slideActive.image) {
          EventBus.$emit('open-add-question-image-modal')
        } else {
          EventBus.$emit('open-edit-question-image-modal')
        }
      }, 250)
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        this.gaLabel,
        'Add/ Change image',
      )
    },
    handleOpenAudioSettingModal() {
      this.setRightNavActiveTabAction(this.rightNavTabNames.AUDIO)
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        this.gaLabel,
        'Add/ Change audio',
      )
      if (this.plan.group == 'free') return
      setTimeout(() => {
        EventBus.$emit('audio: open-audio-modal')
      }, 250)
    },
  },
}
</script>

<style lang="scss" scoped>
.canvas-right-click-menu {
  font-family: Nunito, sans-serif;
  .canvas-right-click-menu-item {
    display: flex !important;
    flex: none !important;
    font-size: 17px;
    padding: 0.5rem;
    &:last-child {
      padding-bottom: 0.25rem !important;
    }
    &.__undo, &.__redo {
      justify-content: space-between;
    }
  }
  &::v-deep .flex {
    display: flex;
  }
  &::v-deep .container-shortcut-key .shortcut-key {
    background: #CECECE;
  }
}
</style>
